<template>
  <div v-if="options" :id="id || undefined">
    <v-autocomplete
        :prepend-icon="prepend_icon || undefined"
        v-if="options"
        clearable
        return-object
        density="compact"
        variant="outlined"
        :rounded="rounded"
        :loading="loading"
        :no-filter="no_filter"
        :hide-no-data="hide_no_data"
        :no-data-text="no_data_text"
        :label="field_label || undefined"
        :style="autocompleteStyle"
        :model-value="selected_value"
        @update:model-value="select"
        v-model:search="search"
        @click:clear="cleared"
        :placeholder="effectivePlaceholder"
        :items="options"
        :item-title="itemText"
        :item-value="resolvedItemValue"
        item-disabled="disabled"
        :disabled="disabled"
        :hint="hint"
        :persistent-hint="persistent_hint"
    ></v-autocomplete>
  </div>

</template>

<script>
export default {
name: "Autocomplete",
  emits: ['select', 'input', 'update:modelValue', 'cleared', 'updated_search_value'],
  props: {
    'id': {default: null},
    'placeholder': {default: ""},
    'options': {required: true},
    /** Vue 2-style binding (still used by many callers via :value / @select). */
    'value': {default: null},
    /** Vue 3 v-model. */
    'modelValue': {default: null},
    'label': {default: 'name'},
    'loading': {default: false},
    'disabled': {default: false},
    'clear_on_select': {default: false},
    'rounded': {default: true},
    'prepend_icon': {default: null},
    'hide_no_data': {type: Boolean, default: false},
    'no_data_text': {type: String, default: ''},
    /** Outlined floating label (same pattern as v-text-field :label in filters). */
    'field_label': {type: String, default: ''},
    'hint': {type: String, default: ''},
    'persistent_hint': {type: Boolean, default: false},
    /** When true, show all :items as given (no extra client-side filter on search text). Use when options are already filtered (e.g. label search). */
    'no_filter': {type: Boolean, default: false},
  },
  computed: {
    boundValue() {
      if (this.modelValue != null && this.modelValue !== '') {
        return this.modelValue;
      }
      return this.value;
    },
    effectivePlaceholder() {
      return this.field_label ? '' : this.placeholder;
    },
    autocompleteStyle() {
      if (this.field_label) {
        return { width: '100%' };
      }
      return { width: '100%', maxWidth: '350px' };
    },
    resolvedItemValue() {
      const opts = this.options || [];
      if (!opts.length) {
        return 'id';
      }
      const first = opts[0];
      if (typeof first !== 'object' || first === null) {
        return undefined;
      }
      if (first.id != null) {
        return 'id';
      }
      if (first.type != null) {
        return 'type';
      }
      if (first.label != null) {
        return 'label';
      }
      return 'id';
    },
    itemText() {
      if (typeof this.label === 'function') {
        return this.label;
      }
      if (typeof this.label === 'string') {
        return (item) => {
          if (!item) {
            return '';
          }
          return this.normalizeItemText(item[this.label] !== undefined ? item[this.label] : item);
        };
      }
      return (item) => this.normalizeItemText(item);
    },
  },
  watch: {
    boundValue: {
      immediate: true,
      handler(n) {
        this.syncFromBound(n);
      },
    },
    search (n) {
      this.$emit('updated_search_value', n);
    }
  },
  data() {
    return {
      search: null,
      selected_value: null
    }
  },
  methods: {
    syncFromBound(n) {
      if (n == null || n === '') {
        if (this.selected_value != null) {
          this.selected_value = null;
        }
        return;
      }
      const next = this.get_id_value(n);
      const curName =
        this.selected_value && typeof this.selected_value === 'object'
          ? this.selected_value.name
          : this.selected_value;
      const nextName =
        next && typeof next === 'object' ? next.name : next;
      if (!this.selected_value || curName !== nextName) {
        this.selected_value = next;
      }
    },
    emitValue(value) {
      this.$emit('select', value);
      this.$emit('input', value);
      this.$emit('update:modelValue', value);
    },
    normalizeItemText(value) {
      if (value === null || value === undefined) {
        return '';
      }
      if (typeof value === 'string' || typeof value === 'number') {
        return String(value);
      }
      if (typeof value === 'object') {
        if (typeof value.name === 'string') {
          return value.name;
        }
        if (typeof value.label === 'string') {
          return value.label;
        }
        if (typeof value.translation === 'string') {
          return value.translation;
        }
        if (typeof value.value === 'string' || typeof value.value === 'number') {
          return String(value.value);
        }
        const fallback = Object.values(value).find(
          (entry) => typeof entry === 'string' || typeof entry === 'number'
        );
        if (fallback !== undefined) {
          return String(fallback);
        }
      }
      return String(value);
    },
    cleared() {
      this.selected_value = null;
      this.search = null;
      this.$emit('cleared');
      this.$emit('input', null);
      this.$emit('update:modelValue', null);
    },
    get_id_value(value) {
      if (value == null || value === '') {
        return null;
      }
      if (typeof value === 'object' && value !== null && value.name != null) {
        return value;
      }
      const opts = this.options || [];
      const byName = opts.find((o) => o && o.name === value);
      if (byName) {
        return byName;
      }
      const byId = opts.find((o) => o != null && o.id == value);
      if (byId) {
        return byId;
      }
      return value;
    },
    select(value) {
      // Vuetify clearable autocomplete often emits null while the user is typing a
      // filter query. Do not push that into v-model — only @click:clear (cleared)
      // should clear the bound value. Otherwise type/parent appear selected in the
      // menu but save still sees null / the previous value.
      if (value == null || value === '') {
        return;
      }
      const opts = this.options || [];
      if (value != null && typeof value === 'object' && !Array.isArray(value)) {
        if (!this.clear_on_select) {
          this.selected_value = value;
        }
        this.emitValue(value);
        if (this.clear_on_select) {
          this.selected_value = null;
          this.search = null;
        }
        return;
      }
      const index = opts.findIndex((option) => option != null && option.id == value);
      if (index === -1) {
        const byName = opts.find((o) => o && o.name === value);
        if (byName) {
          if (!this.clear_on_select) {
            this.selected_value = byName;
          }
          this.emitValue(byName);
          return;
        }
        // Plain string options (e.g. group-by order: random/asc/desc).
        if (!this.clear_on_select && (typeof value === 'string' || typeof value === 'number')) {
          this.selected_value = value;
        }
        this.emitValue(value);
        return;
      }
      const item = opts[index];
      if (!this.clear_on_select) {
        this.selected_value = item;
      }
      this.emitValue(item);
      if (this.clear_on_select) {
        this.selected_value = null;
        this.search = null;
      }
    },
  }
}
</script>

<style scoped>

</style>
